import React from 'react';
import { Button } from '@/components/ui/button';
import { Layers, RefreshCw } from 'lucide-react';

interface AdminReferenceStatsProps {
    allPlacesLoaded: boolean;
    promptAndImageStats: {
        placesWithPrompts: number;
        promptsV2: number;
        promptsV1: number;
        placesWithIncompletePrompts: number;
        placesWithRuinsImages: number;
        totalReconstructionImages: number;
        placesWithIncompleteImages: number;
        totalMissingRuins: number;
        totalMissingReconstructions: number;
        totalPlacesWithoutPrompts: number;
    };
    batchRegeneratingAll: boolean;
    totalPlaces: number;
    totalExistingPlaces: number;
    placeVersionStats?: { v1: number; v2: number };
    costRates: { place: number };
    onBatchGeneratePrompts: () => void;
    onBatchGenerateMissingType?: (type: 'ruins' | 'reconstructions') => void;
    onBatchRegenerateOld: () => void;
}

export const AdminReferenceStats = ({
    allPlacesLoaded,
    promptAndImageStats,
    batchRegeneratingAll,
    totalPlaces,
    totalExistingPlaces,
    placeVersionStats,
    costRates,
    onBatchGeneratePrompts,
    onBatchGenerateMissingType,
    onBatchRegenerateOld
}: AdminReferenceStatsProps) => {
    if (!allPlacesLoaded) return null;

    return (
        <div className="space-y-4">
            {/* Prompts & Images Stats */}
            <div className="grid grid-cols-4 gap-3">
                {/* Image Prompts */}
                <div className="p-3 border rounded-lg bg-blue-50 dark:bg-blue-950/20">
                    <div className="text-xs text-muted-foreground mb-1">Image Prompts</div>
                    <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">{promptAndImageStats.placesWithPrompts}</div>
                    <div className="text-xs text-muted-foreground">
                        {promptAndImageStats.promptsV2} v2, {promptAndImageStats.promptsV1} v1
                        {promptAndImageStats.placesWithIncompletePrompts > 0 && (
                            <span className="text-orange-600 dark:text-orange-400"> · {promptAndImageStats.placesWithIncompletePrompts} incomplete</span>
                        )}
                    </div>
                </div>

                {/* Ruins Images */}
                <div className="p-3 border rounded-lg bg-purple-50 dark:bg-purple-950/20">
                    <div className="text-xs text-muted-foreground mb-1">Ruins Images</div>
                    <div className="text-2xl font-bold text-purple-700 dark:text-purple-400">{promptAndImageStats.placesWithRuinsImages}</div>
                    <div className="text-xs text-muted-foreground">places with ruins</div>
                </div>

                {/* Reconstruction Images */}
                <div className="p-3 border rounded-lg bg-indigo-50 dark:bg-indigo-950/20">
                    <div className="text-xs text-muted-foreground mb-1">Era Images</div>
                    <div className="text-2xl font-bold text-indigo-700 dark:text-indigo-400">{promptAndImageStats.totalReconstructionImages}</div>
                    <div className="text-xs text-muted-foreground">
                        total reconstructions
                        {promptAndImageStats.placesWithIncompleteImages > 0 && (
                            <span className="text-orange-600 dark:text-orange-400"> · {promptAndImageStats.placesWithIncompleteImages} incomplete</span>
                        )}
                    </div>
                </div>

                {/* Coverage */}
                <div className="p-3 border rounded-lg bg-teal-50 dark:bg-teal-950/20">
                    <div className="text-xs text-muted-foreground mb-1">Full Pipeline</div>
                    <div className="text-2xl font-bold text-teal-700 dark:text-teal-400">
                        {promptAndImageStats.placesWithPrompts > 0
                            ? Math.round((promptAndImageStats.placesWithPrompts - promptAndImageStats.placesWithIncompletePrompts - promptAndImageStats.placesWithIncompleteImages) / promptAndImageStats.placesWithPrompts * 100)
                            : 0}%
                    </div>
                    <div className="text-xs text-muted-foreground">complete coverage</div>
                </div>
            </div>

            {/* Batch Actions */}
            <div className="flex items-center gap-2 flex-wrap">
                {promptAndImageStats.totalPlacesWithoutPrompts > 0 && (
                    <Button
                        size="sm"
                        onClick={onBatchGeneratePrompts}
                        disabled={batchRegeneratingAll}
                        variant="default"
                        className="text-xs"
                    >
                        {batchRegeneratingAll ? (
                            <>
                                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                Processing...
                            </>
                        ) : (
                            <>
                                <Layers className="h-3 w-3 mr-1" />
                                Generate Missing Prompts ({promptAndImageStats.totalPlacesWithoutPrompts})
                            </>
                        )}
                    </Button>
                )}

                {promptAndImageStats.totalMissingRuins > 0 && (
                    <Button
                        size="sm"
                        onClick={() => onBatchGenerateMissingType?.('ruins')}
                        disabled={batchRegeneratingAll}
                        variant="outline"
                        className="text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                    >
                        Generate Missing Ruins ({promptAndImageStats.totalMissingRuins})
                    </Button>
                )}

                {promptAndImageStats.totalMissingReconstructions > 0 && (
                    <Button
                        size="sm"
                        onClick={() => onBatchGenerateMissingType?.('reconstructions')}
                        disabled={batchRegeneratingAll}
                        variant="outline"
                        className="text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    >
                        Generate Missing Eras ({promptAndImageStats.totalMissingReconstructions})
                    </Button>
                )}

                {placeVersionStats && placeVersionStats.v1 > 0 && (
                    <Button
                        size="sm"
                        onClick={onBatchRegenerateOld}
                        disabled={batchRegeneratingAll}
                        variant="outline"
                        className="text-xs"
                    >
                        {batchRegeneratingAll ? (
                            <>
                                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                Updating...
                            </>
                        ) : (
                            <>
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Update All v1 → v2 ({placeVersionStats.v1})
                            </>
                        )}
                    </Button>
                )}

                <div className="text-xs text-muted-foreground ml-auto">
                    Est. cost: ${((totalPlaces - totalExistingPlaces) * costRates.place).toFixed(2)} (missing) +
                    ${placeVersionStats ? (placeVersionStats.v1 * costRates.place).toFixed(2) : '0.00'} (updates)
                </div>
            </div>
        </div>
    );
};
